import { useEffect, useState, useMemo, useRef } from "react";
import ApiError from "../../../api/common/apiError";
import { ApiDriversListResponse } from "../../../models/api/admin/driver";
import useDriversListPaginationState from "../../../hooks/useDriversListPaginationState";
import { useDriver } from "../../../api/admin/driver";
import { GetAllDriversDto } from "../../../models/app/admin/driver";
import { type DriversListFiltersFormValues } from "../../../components/DriverListFilters/DriversListFiltersForm";
import {
    driverCategoryOptionsEn,
    type DriverCategoryOptionsType,
    driverPageLocation,
} from "../../../utils/driverPageLocation";
import { isEqual } from "lodash";
import { useCompanyDepartmentSelector } from "../../../components/ReusableComponents/CompanyDepartmentSelector";
import {
    CompanyDashboardFilters,
    FilterPersonalInformationDto,
} from "../../../models/app/admin/driverFilters";
import { driverListFiltersDefaultValues } from "../../../components/DriverListFilters/DriversListFiltersForm/driversListFiltersDefaultValues";
import { useDriversStats } from "../../../api/admin/driverStats";
import useDebounceValue from "../../../hooks/useDebounceValue";
import { debounce } from "@mui/material";
import { SEARCH_BAR_DEBOUNCE_MS } from "../../../constants/filterConstants";
import useTableStore from "../../../stores/useTableStore";
import {
    tableHeaderOptionsEnum,
    TableHeaderOptionsType,
    useTableHeaders,
} from "../../../hooks/useTableHeaders";
import { match } from "ts-pattern";
import { filterVisibleColumnIds } from "../../../utils/filterVisibleColumnIds";
import { createDriverTableSortingProperties } from "../../../utils/createDriverTableSortingProperties";

const useDriversPage = (locationPath: string, skipFetch = false) => {
    const location = driverPageLocation(
        locationPath
    ) as DriverCategoryOptionsType;
    const defaultFilterVals = driverListFiltersDefaultValues(location);
    // using useRef so that comparison between old/new body is synchronous
    const currentBody = useRef<GetAllDriversDto | undefined>(undefined);

    const setAppliedFilter = useTableStore((state) => state.setAppliedFilter);
    const getAppliedFilter = useTableStore((state) => state.getAppliedFilter);
    const appliedFilterAllDrivers = useTableStore(
        (state) => state.appliedFilterAllDrivers
    );
    const appliedFilterProfessionalDrivers = useTableStore(
        (state) => state.appliedFilterProfessionalDrivers
    );
    const appliedFilterGreyFleet = useTableStore(
        (state) => state.appliedFilterGreyFleet
    );
    const appliedFilter = getAppliedFilter(location);
    const setIsRedirect = useTableStore((state) => state.setIsRedirect);
    const isRedirect = useTableStore((state) => state.isRedirect);

    const hiddenColsGreyFleet = useTableStore(
        (state) => state.hiddenColsGreyFleet
    );
    const hiddenColsAllDrivers = useTableStore(
        (state) => state.hiddenColsAllDrivers
    );
    const hiddenColsProDrivers = useTableStore(
        (state) => state.hiddenColsProfessionalDrivers
    );

    const hiddenColsForCurrentLocation = match(location)
        .with(driverCategoryOptionsEn.drivers, () => hiddenColsAllDrivers)
        .with(
            driverCategoryOptionsEn.professionalDrivers,
            () => hiddenColsProDrivers
        )
        .with(driverCategoryOptionsEn.greyfleet, () => hiddenColsGreyFleet)
        .exhaustive();

    const filteredVisibleTableHeaderIds = useMemo(() => {
        return filterVisibleColumnIds(hiddenColsForCurrentLocation);
    }, [hiddenColsForCurrentLocation]);

    const tableIdentifier: TableHeaderOptionsType = match(location)
        .with(
            driverCategoryOptionsEn.drivers,
            () => tableHeaderOptionsEnum.allDrivers
        )
        .with(
            driverCategoryOptionsEn.professionalDrivers,
            () => tableHeaderOptionsEnum.professionalDrivers
        )
        .with(
            driverCategoryOptionsEn.greyfleet,
            () => tableHeaderOptionsEnum.greyFleet
        )
        .otherwise(() => tableHeaderOptionsEnum.declarations);

    const { updateTableConfig } = useTableHeaders(tableIdentifier, !isRedirect);

    useEffect(() => {
        if (isRedirect) {
            setIsRedirect(false);
        } else {
            /** update initial table columns */
            updateTableConfig();
            /** Need to filter incoming data by page requirements like isProfessional etc */
            setAppliedFilter(
                { ...getAppliedFilter(location), ...defaultFilterVals },
                location
            );
        }
    }, []);

    const employmentFilters = useCompanyDepartmentSelector();
    const { selectedDepartment, selectedCompany } = employmentFilters;

    // state for filter form & modal
    const [filterModalOpen, setFilterModalOpen] = useState<
        "add" | "edit" | false
    >(false);

    // the list of drivers from the api and its error
    const [driversList, setDriversList] = useState<
        ApiDriversListResponse | undefined
    >();

    const [fetchDriversError, setFetchDriversError] = useState<
        ApiError | undefined
    >();

    const paginationState = useDriversListPaginationState(location);

    const { getAllDrivers, isGettingAllDrivers } = useDriver();

    const { driverStatsData, isLoadingDriverStats } = useDriversStats(
        location === "professionalDrivers",
        selectedCompany?.companyId,
        selectedDepartment?.departmentId
    );

    const createEmploymentFilters = (
        employeeId?: string | null,
        companyDashboard?: CompanyDashboardFilters,
        companyId?: string,
        departmentId?: string
    ) => {
        if (!employeeId && !companyDashboard && !companyId && !departmentId) {
            return undefined;
        }

        const companyFilters: Pick<DriversListFiltersFormValues, "company"> = {
            company: {
                ...(companyId && {
                    companyIds: [companyId],
                }),
                ...(departmentId && {
                    departmentIds: [departmentId],
                }),
                ...(employeeId && { employeeId }),
                ...(companyDashboard && { companyDashboard }),
            },
        };
        return companyFilters;
    };

    /* search query will be part of the whole filter,
        but will need to refresh when the component refreshes,
        otherwise stay applied. */
    const [query, setQuery] = useState<string | null>(null);
    const searchQuery = useDebounceValue(query, SEARCH_BAR_DEBOUNCE_MS);

    // 03/04/24 debouncing to prevent duplicate calls made by useEffects
    const debouncedHandleFetchDrivers = debounce(
        (filters?: DriversListFiltersFormValues) => {
            handleFetchDrivers(filters);
        },
        0
    );

    const createGetAllDriversBody = (
        filters: DriversListFiltersFormValues | undefined,
        personalInformationFromFilterOrQuery:
            | FilterPersonalInformationDto
            | undefined,
        employeeIdFromFilter: string | null | undefined,
        companyDashboardFromFilter: CompanyDashboardFilters | undefined,
        companyId: string | undefined,
        departmentId: string | undefined,
        reportFormatType?: string
    ): GetAllDriversDto => {
        // certain columns have a special sorting logic, this function handles that
        const sortingProperties = createDriverTableSortingProperties(
            paginationState.orderBy,
            paginationState.sortOrder
        );

        return {
            pageNumber: paginationState.pageNumber,
            pageSize: paginationState.pageSize,
            selectedColumns: filteredVisibleTableHeaderIds,
            ...sortingProperties,
            ...(filters && { ...filters }),
            // apply the query when we search, rather than saving it with the rest of the filter
            ...(personalInformationFromFilterOrQuery && {
                personalInformation: personalInformationFromFilterOrQuery,
            }),
            // 18/07/23 NB: Client/Dept are filters via the Dropdowns at top of page, hence:
            ...createEmploymentFilters(
                employeeIdFromFilter,
                companyDashboardFromFilter,
                companyId,
                departmentId
            ),
            ...(reportFormatType && { reportFormatType }),
        };
    };

    const constructAllDriversBody = (
        filters?: DriversListFiltersFormValues
    ): GetAllDriversDto => {
        // If landing on page with a filter applied, use that filter until dept/company is changed
        const companyIdFromFilter = filters?.company?.companyIds?.[0];
        const companyId = selectedCompany?.companyId ?? companyIdFromFilter;
        const departmentIdFromFilter = filters?.company?.departmentIds?.[0];
        const departmentId =
            selectedDepartment?.departmentId ?? departmentIdFromFilter;

        const employeeIdFromFilter = filters?.company?.employeeId;
        const companyDashboardFromFilter = filters?.company?.companyDashboard;

        const filterPersonalInformation = filters?.personalInformation
            ? { ...filters?.personalInformation }
            : undefined;

        const personalInformationFromFilterOrQuery = searchQuery
            ? {
                  ...(filters?.personalInformation &&
                      filters.personalInformation),
                  firstName: searchQuery,
                  lastName: searchQuery,
                  email: searchQuery,
              }
            : filterPersonalInformation;

        return createGetAllDriversBody(
            filters,
            personalInformationFromFilterOrQuery,
            employeeIdFromFilter,
            companyDashboardFromFilter,
            companyId,
            departmentId
        );
    };

    const constructAllDriversBodyForReport = (
        filters?: DriversListFiltersFormValues,
        reportFormatType?: string,
        companyId?: string,
        departmentId?: string
    ): GetAllDriversDto => {
        const employeeIdFromFilter = filters?.company?.employeeId;
        const companyDashboardFromFilter = filters?.company?.companyDashboard;

        const filterPersonalInformation = filters?.personalInformation
            ? { ...filters?.personalInformation }
            : undefined;

        const personalInformationFromFilterOrQuery = searchQuery
            ? {
                  ...(filters?.personalInformation &&
                      filters.personalInformation),
                  firstName: searchQuery,
                  lastName: searchQuery,
                  email: searchQuery,
              }
            : filterPersonalInformation;

        return createGetAllDriversBody(
            filters,
            personalInformationFromFilterOrQuery,
            employeeIdFromFilter,
            companyDashboardFromFilter,
            companyId,
            departmentId,
            reportFormatType
        );
    };

    const handleFetchDrivers = async (
        filters?: DriversListFiltersFormValues
    ) => {
        /**
         * Filter by incoming filter values if form is submitted,
         * use existing global filters if loading for first time
         */
        const getAllDriversBody = constructAllDriversBody(filters);

        const bodyHasNotChanged = isEqual(
            getAllDriversBody,
            currentBody.current
        );

        if (bodyHasNotChanged || isGettingAllDrivers) return true;

        currentBody.current = getAllDriversBody;

        const response = await getAllDrivers(getAllDriversBody);

        if (response.success) {
            setFetchDriversError(undefined);
            setDriversList(response.content);

            if (filters) {
                setFilterModalOpen(false);
            }
        } else if (!response.success) {
            setFetchDriversError(response.error);
        }
        return response.success;
    };

    // if page number or page size etc changes, fetch drivers.
    useEffect(() => {
        const hiddenColsStatePopulated = !!hiddenColsForCurrentLocation.length;
        if (!skipFetch && hiddenColsStatePopulated) {
            const filter = {
                ...defaultFilterVals,
                ...appliedFilter.filterFields,
            };
            debouncedHandleFetchDrivers(filter);
        }
    }, [
        JSON.stringify(selectedCompany),
        JSON.stringify(selectedDepartment),
        searchQuery,
        appliedFilterAllDrivers,
        appliedFilterProfessionalDrivers,
        appliedFilterGreyFleet,
        paginationState.orderBy,
        paginationState.sortOrder,
        paginationState.pageNumber,
        paginationState.pageSize,
        location,
        filteredVisibleTableHeaderIds,
    ]);

    // if the selected company/dept changes, remove any company/dept filters from the applied filter:
    // 11/08/23 NB: if all filters depended on applied filter as a single source of truth, this
    // ...useEffect would not be necessary
    useEffect(() => {
        const companyIdFromFilter =
            appliedFilter.filterFields?.company?.companyIds?.[0];
        const departmentIdFromFilter =
            appliedFilter.filterFields?.company?.departmentIds?.[0];

        const userHasSelectedCompanyOrDept =
            selectedCompany?.companyId || selectedDepartment?.departmentId;
        const companyOrDeptFilterIsApplied =
            companyIdFromFilter || departmentIdFromFilter;

        const hiddenColsStatePopulated = !!hiddenColsForCurrentLocation.length;

        if (userHasSelectedCompanyOrDept && companyOrDeptFilterIsApplied) {
            // remove company filter from applied filter
            const currentlyAppliedFilters = appliedFilter.filterFields;
            const currentFilterId = appliedFilter.filterId;
            const appliedFiltersWithoutCompanyFilters = {
                filterId: currentFilterId,
                filterFields: {
                    ...currentlyAppliedFilters,
                    company: defaultFilterVals.company,
                },
            };
            setAppliedFilter(appliedFiltersWithoutCompanyFilters, location);
            if (!skipFetch && hiddenColsStatePopulated) {
                debouncedHandleFetchDrivers(
                    appliedFiltersWithoutCompanyFilters.filterFields
                );
            }
        } else if (
            !companyOrDeptFilterIsApplied &&
            userHasSelectedCompanyOrDept &&
            !skipFetch &&
            hiddenColsStatePopulated
        ) {
            debouncedHandleFetchDrivers(appliedFilter.filterFields);
        }
    }, [
        selectedCompany?.companyId,
        selectedDepartment?.departmentId,
        appliedFilter,
    ]);

    return {
        filterModalOpen,
        setFilterModalOpen,
        driversList,
        fetchDriversError,
        paginationState,
        handleFetchDrivers,
        isFetchingDrivers: isGettingAllDrivers,
        constructAllDriversBodyForReport,
        driverPageLocation: location,
        driverStatsData,
        isLoadingDriverStats,
        employmentFilters,
        query,
        setQuery,
    };
};

export default useDriversPage;
